import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { IProductFamily, IProductLine } from '../model/pfpl.model';
import { EnvironmentConfig } from '../model/environment.model';
import { ENV_CONFIG } from '../injection-tokens/environment.token';

@Injectable({
    providedIn: 'root',
})
export class PFPLService {
    private _httpClient: HttpClient = inject(HttpClient);
    private _environment: EnvironmentConfig = inject(ENV_CONFIG);
    private readonly PRODUCT_FAMILY_BASE_URL: string;
    private readonly PRODUCT_LINE_BASE_URL: string;
    private readonly ORG_LINE_BASE_URL: string;

    constructor() {
        this.PRODUCT_FAMILY_BASE_URL = `${this._environment.APP.VC_STORE_BASE_URL}/product-family`;
        this.PRODUCT_LINE_BASE_URL = `${this._environment.APP.VC_STORE_BASE_URL}/product-line`;
        this.ORG_LINE_BASE_URL = `${this._environment.APP.VC_STORE_BASE_URL}/org-line`;
    }

    public createFamily(model: IProductFamily): Observable<IProductFamily> {
        return this._httpClient.post<IProductFamily>(this.PRODUCT_FAMILY_BASE_URL, model);
    }

    public updateFamily(model: IProductFamily): Observable<IProductFamily> {
        return this._httpClient.put<IProductFamily>(`${this.PRODUCT_FAMILY_BASE_URL}/${model.id}`, model);
    }

    public getFamilies(
        params: {
            includeLines?: boolean;
            excludeEmptyFamilies?: boolean;
            orgId?: string;
        } = {}
    ): Observable<IProductFamily[]> {
        const queryParams: Record<string, boolean | string> = {
            'include-lines': !!params.includeLines,
            'exclude-empty': !!params.excludeEmptyFamilies,
        };

        if (params.orgId) {
            queryParams['org-id'] = params.orgId;
        }

        return this._httpClient.get<IProductFamily[]>(this.PRODUCT_FAMILY_BASE_URL, {
            params: queryParams,
        });
    }

    public getFamilyById(familyId: number): Observable<IProductFamily> {
        return this._httpClient.get<IProductFamily>(`${this.PRODUCT_FAMILY_BASE_URL}/${familyId}`);
    }

    public getFamilyLines(familyId: number): Observable<IProductLine[]> {
        return this._httpClient.get<IProductLine[]>(`${this.PRODUCT_FAMILY_BASE_URL}/family/${familyId}`);
    }

    public removeFamily(familyId: number): Observable<IProductFamily> {
        return this._httpClient.delete<IProductFamily>(`${this.PRODUCT_FAMILY_BASE_URL}/${familyId}`);
    }

    public getLine(lineId: number, includeProducts = false): Observable<IProductLine> {
        return this._httpClient.get<IProductLine>(`${this.PRODUCT_LINE_BASE_URL}/${lineId}`, {
            params: {
                'include-products': includeProducts,
            },
        });
    }

    public getLineByOrgId(orgId: string): Observable<{ orgId: string; lines: IProductLine[]; isPrimary: boolean }> {
        return this._httpClient.get<{ orgId: string; lines: IProductLine[]; isPrimary: boolean }>(
            `${this.ORG_LINE_BASE_URL}/${orgId}`
        );
    }

    public assignProductLines(orgId: string, productLines: number[]): Observable<void> {
        return this._httpClient.put<void>(`${this.ORG_LINE_BASE_URL}/${orgId}`, productLines);
    }

    public getProductLines(): Observable<IProductLine[]> {
        return this._httpClient.get<IProductLine[]>(`${this.PRODUCT_LINE_BASE_URL}`);
    }

    public createLine(model: IProductLine): Observable<IProductLine> {
        return this._httpClient.post<IProductLine>(this.PRODUCT_LINE_BASE_URL, model);
    }

    public updateLine(model: IProductLine): Observable<IProductLine> {
        return this._httpClient.put<IProductLine>(`${this.PRODUCT_LINE_BASE_URL}/${model.id}`, model);
    }

    public removeLine(lineId: number): Observable<IProductLine> {
        return this._httpClient.delete<IProductLine>(`${this.PRODUCT_LINE_BASE_URL}/${lineId}`);
    }
}
